/**
 * Generalized content-hub filter model + tree compiler.
 *
 * Every existing filter schema (saved filter, watchlist, quick search box)
 * maps *into* `ContentHubFilter` via a small per-type adapter; only this shape
 * reaches `buildTree`, which compiles it to the declarative filter tree the
 * content-hub `POST /v1/search` body accepts.
 *
 * The tree DSL — never raw ES. A node is a leaf (field predicates) or a group
 * ({ combine, conditions, exclude }). The server rejects off-contract bodies
 * with 422.
 */
import type { ContentHubSource } from "./config";

/** The two date columns content-hub `/v1/search` can range-filter on.
 *  `occurs_at` is the event's business/publish time (what recency sorts on);
 *  `created_at` is the source-declared creation time. */
export type ContentHubDateField = "occurs_at" | "created_at";

/**
 * A date-range window on one date column. Bounds are ISO-8601 strings
 * (e.g. "2026-06-01T00:00:00Z", or date-only "2026-06-01") or epoch-**ms**
 * integers; either side may be omitted for an open-ended range. Sent as UTC —
 * a bare string with no timezone is interpreted as UTC by the backend.
 */
export interface ContentHubDateRange {
  /** Which date column to filter. Default "occurs_at". */
  field?: ContentHubDateField;
  /** Inclusive lower bound → compiled to `gte`. */
  from?: string | number;
  /** Inclusive upper bound → compiled to `lte`. */
  to?: string | number;
}

export interface ContentHubFilter {
  /** Free text to match (stemmed by default). Multi-word entries become
   *  adjacent-`phrase` leaves — set `keywordsExact` for a literal match. */
  keywords?: string[];
  /** How keyword terms combine. Default "or". */
  keywordsOp?: "and" | "or";
  /** Exact-match (verbatim) mode: emit every keyword term as a `verbatim` leaf
   *  (`{ text: [...], verbatim: true }`) — a literal, case-insensitive match on
   *  the unstemmed `search_exact` umbrella (no stemming, no stopwords), where a
   *  multi-word entry matches as an exact phrase. Applies regardless of word
   *  count, so a single word is matched literally too. This is a USER TOGGLE,
   *  never inferred from whitespace — `verbatim` is a no-op for a genuinely
   *  single token, so it is always safe to pass straight from the checkbox.
   *  Off → the default stemmed `text` path (single words collapse into one
   *  OR-ed `text` leaf; multi-word entries become adjacent-`phrase` leaves).
   *  Default false. */
  keywordsExact?: boolean;
  /** Tag ids (stringify numeric ids). */
  tags?: string[];
  /** Ticker symbols (server uppercases/trims). */
  tickers?: string[];
  /** backend_source values — a view usually pins this. */
  sources?: string[];
  /** Source-defined importance strings, e.g. ["1.0"]. */
  importance?: string[];
  /** Date-range window on `occurs_at` (default) or `created_at`. */
  dateRange?: ContentHubDateRange;
  /** Dimensions to NOT-match (compiled into the group's `exclude`). */
  exclude?: Pick<ContentHubFilter, "keywords" | "tickers" | "tags" | "sources">;
  /** Escape hatch: pre-built filter-tree leaves AND-ed into the top-level group
   *  verbatim. For source-specific typed columns the generalized dimensions
   *  above don't model — e.g. the equities data-event columns (`opt_premium`,
   *  `gov_txn_type`, …). Each entry is a raw leaf (a numeric range, keyword
   *  terms, or date range on a named column); the caller is responsible for its
   *  correctness (an off-contract column/shape is rejected with 422). */
  raw?: FilterTreeNode[];
}

/** A compiled tree node — a leaf or a group. Kept loose on purpose: the shape
 *  is validated by the server, and buildTree only ever emits DSL keys. */
export type FilterTreeNode = Record<string, unknown>;

const cleanTerms = (values?: string[]): string[] =>
  (values ?? []).map((v) => `${v}`.trim()).filter(Boolean);

const isMultiWord = (term: string): boolean => /\s/.test(term.trim());

/**
 * Compile the keyword dimension.
 *
 * Exact-match (`verbatim`) mode: every term becomes a `{ text: [term],
 * verbatim: true }` leaf regardless of word count — a literal, unstemmed,
 * case-insensitive match where a multi-word entry is an exact phrase (e.g.
 * `{ text: ["quantitative easing"], verbatim: true }`). Driven only by the
 * caller's toggle, never inferred from whitespace.
 *
 * Default (stemmed) mode: single-word terms collapse into one `text` leaf (OR
 * within the leaf); each multi-word term becomes its own adjacent-`phrase` leaf.
 *
 * Either way, when more than one leaf results they are wrapped in a group
 * combined per `keywordsOp`.
 */
function compileKeywords(
  keywords: string[] | undefined,
  keywordsOp: "and" | "or" | undefined,
  exact = false
): FilterTreeNode | null {
  const terms = cleanTerms(keywords);
  if (terms.length === 0) return null;

  const leaves: FilterTreeNode[] = [];
  if (exact) {
    // Exact match: force a verbatim leaf for every term, single words included.
    for (const term of terms) leaves.push({ text: [term], verbatim: true });
  } else {
    const singleWords = terms.filter((t) => !isMultiWord(t));
    const phrases = terms.filter(isMultiWord);
    if (singleWords.length > 0) leaves.push({ text: singleWords });
    for (const p of phrases) leaves.push({ text: [p], phrase: true });
  }

  if (leaves.length === 1) return leaves[0];
  return {
    combine: keywordsOp === "and" ? "all" : "any",
    conditions: leaves,
  };
}

/** Normalize a single date bound: trim strings (drop empties), keep finite
 *  epoch-ms numbers. Anything else is dropped so we never emit a garbage bound
 *  the server would 422 on. */
function cleanBound(value?: string | number): string | number | undefined {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  }
  return undefined;
}

/**
 * Compile a date-range window into a single leaf, e.g.
 * `{ occurs_at: { gte: "…", lte: "…" } }`. Each present bound maps to an op
 * (`from`→`gte`, `to`→`lte`); an absent side is simply omitted (open-ended).
 * Returns null when neither bound survives cleaning.
 */
function compileDateRange(range?: ContentHubDateRange): FilterTreeNode | null {
  if (!range) return null;

  const ops: Record<string, string | number> = {};
  const from = cleanBound(range.from);
  if (from !== undefined) ops.gte = from;
  const to = cleanBound(range.to);
  if (to !== undefined) ops.lte = to;

  if (Object.keys(ops).length === 0) return null;

  const field: ContentHubDateField = range.field ?? "occurs_at";
  return { [field]: ops };
}

/** Compile the non-keyword dimensions of a filter into a flat list of leaves. */
function compileDimensions(
  filter: Pick<
    ContentHubFilter,
    | "keywords"
    | "keywordsOp"
    | "keywordsExact"
    | "tags"
    | "tickers"
    | "sources"
    | "importance"
    | "dateRange"
  >
): FilterTreeNode[] {
  const nodes: FilterTreeNode[] = [];

  const keywordNode = compileKeywords(
    filter.keywords,
    filter.keywordsOp,
    filter.keywordsExact
  );
  if (keywordNode) nodes.push(keywordNode);

  const tags = cleanTerms(filter.tags);
  if (tags.length > 0) nodes.push({ tags });

  const tickers = cleanTerms(filter.tickers);
  if (tickers.length > 0) nodes.push({ tickers });

  const importance = cleanTerms(filter.importance);
  if (importance.length > 0) nodes.push({ importance });

  const sources = cleanTerms(filter.sources);
  if (sources.length > 0) nodes.push({ backend_source: sources });

  const dateNode = compileDateRange(filter.dateRange);
  if (dateNode) nodes.push(dateNode);

  return nodes;
}

/**
 * Compile a `ContentHubFilter` to a content-hub filter tree.
 *
 * All dimensions are AND-ed under a top-level `combine: "all"` group; the
 * pinned source (e.g. the headlines list) is just one more `backend_source`
 * leaf AND-ed in. `exclude` dimensions compile the same way into the group's
 * `exclude` array. Empty dimensions are omitted; an all-empty filter returns
 * just the pinned-source leaf (or `{}` for a cross-source view).
 */
export function buildTree(
  filter: ContentHubFilter,
  pinnedSource?: ContentHubSource
): FilterTreeNode {
  const conditions = compileDimensions(filter);
  if (pinnedSource) {
    conditions.unshift({ backend_source: [pinnedSource] });
  }
  // Raw leaves (typed source-specific columns) AND-ed in verbatim.
  if (filter.raw && filter.raw.length > 0) {
    conditions.push(...filter.raw);
  }

  const exclude = filter.exclude ? compileDimensions(filter.exclude) : [];

  // No conditions at all → matches everything.
  if (conditions.length === 0 && exclude.length === 0) return {};

  // A single leaf (typically just the pinned source) needs no wrapping group,
  // but a lone exclude still needs a group to attach to.
  if (conditions.length === 1 && exclude.length === 0) return conditions[0];

  const group: FilterTreeNode = { combine: "all", conditions };
  if (exclude.length > 0) group.exclude = exclude;
  return group;
}

const sortedUnique = (values?: string[]): string[] =>
  Array.from(new Set(cleanTerms(values))).sort();

/**
 * Normalize a filter into a stable, order-independent value for React Query
 * keys. Two equivalent filters (same values, different entry order) normalize
 * equal, so they share one cache entry. Empty dimensions are dropped.
 */
export function normalizeFilter(
  filter: ContentHubFilter
): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  const keywords = cleanTerms(filter.keywords);
  if (keywords.length > 0) {
    // Keyword order is significant to phrasing but not to identity — sort.
    out.keywords = [...keywords].sort();
    out.keywordsOp = filter.keywordsOp === "and" ? "and" : "or";
    // Exact-match toggles the compiled leaves (verbatim vs stemmed), so it must
    // discriminate the cache key even when the keyword text is identical.
    if (filter.keywordsExact) out.keywordsExact = true;
  }

  const tags = sortedUnique(filter.tags);
  if (tags.length > 0) out.tags = tags;

  const tickers = sortedUnique(filter.tickers);
  if (tickers.length > 0) out.tickers = tickers;

  const sources = sortedUnique(filter.sources);
  if (sources.length > 0) out.sources = sources;

  const importance = sortedUnique(filter.importance);
  if (importance.length > 0) out.importance = importance;

  if (filter.dateRange) {
    const from = cleanBound(filter.dateRange.from);
    const to = cleanBound(filter.dateRange.to);
    if (from !== undefined || to !== undefined) {
      const dateRange: Record<string, unknown> = {
        field: filter.dateRange.field ?? "occurs_at",
      };
      if (from !== undefined) dateRange.from = from;
      if (to !== undefined) dateRange.to = to;
      out.dateRange = dateRange;
    }
  }

  // Raw leaves are already normalized DSL objects; include them verbatim so
  // equivalent raw sets share a cache entry + one live-filter subscription.
  // (React Query's hashKey sorts object keys, so leaf key order is irrelevant;
  // the array order is deterministic per caller.)
  if (filter.raw && filter.raw.length > 0) {
    out.raw = filter.raw;
  }

  if (filter.exclude) {
    const exclude = normalizeFilter(filter.exclude);
    if (Object.keys(exclude).length > 0) out.exclude = exclude;
  }

  return out;
}

/** True when no dimension of the filter carries any value. */
export function isContentHubFilterEmpty(filter: ContentHubFilter): boolean {
  return Object.keys(normalizeFilter(filter)).length === 0;
}
